import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useEdit from 'hooks/utils/useEdit';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch
} from 'react-router-dom';
import {
  ROUTE_EXERCISES_TEMPLATES_ENTITY,
  ROUTE_EXERCISES_TEMPLATES_ENTITY_TEMPLATE_DETAILS_EDIT
} from 'routes/RouteList';
import TemplateForm from 'containers/pages/exercises/templates/TemplateForm';
import { CardHeader, CardHeaderTitle } from 'components/card/card-header';
import messages from 'messages';
import FormEditHeaderButtons from 'containers/partials/buttons/FormEditHeaderButtons';
import { CardBody } from 'components/card';
import FormEditFooterButtons from 'containers/partials/buttons/FormEditFooterButtons';
import FieldInput from 'components/input/FieldInput';
import Tags from 'containers/partials/inputs/Tags';
import TagMultiSelect from 'containers/partials/inputs/TagMultiSelect';
import { TemplateType } from 'constants';
import { useStore } from 'stores/Store';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import RemoveExerciseTemplateButton from 'containers/partials/buttons/RemoveExerciseTemplateButton';
import useTemplates from 'hooks/queries/useTemplates';
import { useNotificationQueue } from 'components/notification';

const TemplateDetailsTab = ({ onCancel }) => {
  const intl = useIntl();
  const { entityId, templateId } = useParams();
  const { setMenuItems, clearAllContext } = useContextMenu();
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  const notification = useNotificationQueue();

  const { fetchTemplates } = useTemplates(entityId);
  const template = useStore(state => state.template);

  const [edit, setEdit] = useEdit(
    !!useRouteMatch([ROUTE_EXERCISES_TEMPLATES_ENTITY_TEMPLATE_DETAILS_EDIT])
  );

  useEffect(() => {
    const contextButtons = [];

    if (ability.can('delete', template)) {
      contextButtons.push(<DeleteTemplateBtn />);
    }

    setMenuItems(contextButtons);

    return () => {
      clearAllContext();
    };
  }, [template]);

  const onDeletedHandler = async () => {
    notification.add(templateId, {
      message: intl.formatMessage(messages.messageRemovedTemplateSuccess)
    });
    await fetchTemplates({ fetchPolicy: 'network-only' });
    push(
      generatePath(ROUTE_EXERCISES_TEMPLATES_ENTITY, {
        entityId
      })
    );
  };

  const DeleteTemplateBtn = () => (
    <RemoveExerciseTemplateButton
      menu
      templateId={template.id}
      collectionType={TemplateType.TEMPLATE}
      entityId={entityId}
      onDeleted={onDeletedHandler}
    >
      <FormattedMessage {...messages.exercisesRemoveTemplateButton} />
    </RemoveExerciseTemplateButton>
  );

  return (
    <TemplateForm
      entityId={entityId}
      template={template}
      type={TemplateType.TEMPLATE}
      extraClassNames={
        'c-card c-card--secondary c-card--multiple-body c-card--multiple-body-with-tags'
      }
      onComplete={() => setEdit(false)}
    >
      {({
        values: template,
        resetForm,
        submitForm,
        errors,
        touched,
        setFieldValue
      }) => {
        return (
          <>
            <CardHeader secondary>
              <CardHeaderTitle>
                <FormattedMessage {...messages.programDetailTitle} />
              </CardHeaderTitle>
              <FormEditHeaderButtons
                edit={edit}
                setEdit={setEdit}
                onCancel={resetForm}
                onSubmit={submitForm}
              />
            </CardHeader>
            <CardBody secondary separatorBottom>
              <FieldInput
                id="title"
                name="title"
                placeholder={intl.formatMessage(
                  messages.templateNamePlaceholder
                )}
                errors={errors}
                touched={touched}
                readOnly={!edit}
              >
                <FormattedMessage {...messages.templateNameLabel} />
              </FieldInput>
            </CardBody>

            <CardHeader secondary>
              <CardHeaderTitle>
                <FormattedMessage {...messages.exercisesLabelTags} />
              </CardHeaderTitle>
            </CardHeader>

            {edit ? (
              <CardBody secondary separatorBottom>
                <TagMultiSelect
                  entityId={entityId}
                  onChange={tags => {
                    setFieldValue('tags', tags);
                  }}
                  selectedTags={template.tags}
                />
              </CardBody>
            ) : (
              <CardBody secondary separatorBottom>
                <Tags
                  entityId={entityId}
                  selectedTags={template.tags}
                  isMulti
                  isClearable={false}
                  edit={false}
                />
              </CardBody>
            )}
            {edit && (
              <FormEditFooterButtons
                extraClassNames={'c-card__footer-bottom-fixed'}
                edit={edit}
                setEdit={edit => setEdit(edit)}
                onCancel={() => {
                  resetForm();
                  onCancel && onCancel();
                }}
                onSubmit={submitForm}
                type={'button'}
              />
            )}
          </>
        );
      }}
    </TemplateForm>
  );
};

export default TemplateDetailsTab;
